import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/toast';
import type { FormExtrasContext } from '@/components/shared/crud-page';
import { useConfirm } from '@/hooks/use-confirm';
import { LookupField } from '@/components/shared/lookup-field';

interface StagedProduct {
  catalogueItem: string;
  itemCode?: string;
  description?: string;
  quantity: number;
  unit?: string;
}

interface PersistedProduct {
  id: string;
  catalogueItem?: { id: string; itemCode?: string; description?: string };
  quantity?: number;
  unit?: string;
}

// Inline catalogue-items subform for the Dispatch Jobs CRUD dialog.
// In create mode: stages items in formValues._stagedProducts; the parent CrudConfig.afterSave
// hook below POSTs them once the parent record id is known.
// In edit mode: the existing line items are loaded from /dispatch-job-products and
// can be added to / removed inline against the live records.
export function DispatchJobProductsSubform({ isEdit, parentId, formValues, setFormValues }: FormExtrasContext) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  // The catalogue is fetched on-demand by the LookupField below — no
  // all-items useQuery here (the 500-item cap was breaking once the
  // catalogue grew past that). The picked CatalogueItem is held in
  // local state via `onPick` so addRow can carry itemCode + description
  // into staged products without needing a label cache.
  const { data: units = [] } = useQuery<any[]>({
    queryKey: ['/master-data/units'],
    queryFn: async () => (await api<any[]>('/master-data/units?limit=200')).data || [],
  });

  // Edit-mode: load persisted children. Defensively coerce to an array — a
  // transient error response (e.g. 429 rate-limit while the driver page is
  // also polling) returns `{ success: false, error: {...} }` with no `data`
  // field; `r.data || []` already handles undefined, but Array.isArray is
  // the robust gate against any non-array sneaking through.
  const { data: persistedRaw } = useQuery<PersistedProduct[]>({
    queryKey: ['/dispatch-job-products', parentId],
    queryFn: async () => {
      const r = await api<PersistedProduct[]>(`/dispatch-job-products?dispatchJob=${parentId}&limit=200`);
      return Array.isArray(r.data) ? r.data : [];
    },
    enabled: isEdit && !!parentId,
  });
  const persisted: PersistedProduct[] = Array.isArray(persistedRaw) ? persistedRaw : [];

  const addPersisted = useMutation({
    mutationFn: async (p: { catalogueItem: string; quantity: number; unit?: string }) => {
      const r = await api('/dispatch-job-products', {
        method: 'POST',
        body: JSON.stringify({ dispatchJob: parentId, ...p, unit: p.unit ?? null }),
      });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['/dispatch-job-products', parentId] }),
    onError: (e: any) => toast('Error', e.message, 'error'),
  });
  const delPersisted = useMutation({
    mutationFn: async (id: string) => {
      const r = await api(`/dispatch-job-products/${id}`, { method: 'DELETE' });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['/dispatch-job-products', parentId] }),
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  const staged: StagedProduct[] = formValues._stagedProducts ?? [];
  const setStaged = (next: StagedProduct[]) => {
    setFormValues((prev) => ({ ...prev, _stagedProducts: next }));
  };

  // newItem holds the full picked CatalogueItem (via LookupField's onPick)
  // so the staged-products path can carry itemCode + description forward
  // without a label cache. Cleared to null when the row is added.
  const [newItem, setNewItem] = React.useState<any | null>(null);
  const [newQty, setNewQty] = React.useState('');
  const [newUnit, setNewUnit] = React.useState('');

  function clearAdd() { setNewItem(null); setNewQty(''); setNewUnit(''); }

  function addRow() {
    if (!newItem || !newQty) return;
    if (isEdit && parentId) {
      addPersisted.mutate(
        { catalogueItem: newItem.id, quantity: Number(newQty), unit: newUnit || undefined },
        { onSuccess: clearAdd },
      );
      return;
    }
    setStaged([...staged, {
      catalogueItem: newItem.id,
      itemCode: newItem.itemCode,
      description: newItem.description,
      quantity: Number(newQty),
      unit: newUnit || undefined,
    }]);
    clearAdd();
  }

  const rows: Array<{
    key: string;
    itemCode?: string;
    description?: string;
    quantity?: number;
    unit?: string | null;
    onRemove: () => void;
  }> = isEdit
    ? persisted.map((p) => ({
        key: p.id,
        itemCode: p.catalogueItem?.itemCode,
        description: p.catalogueItem?.description,
        quantity: p.quantity,
        unit: p.unit,
        onRemove: async () => { if (await confirm({ title: 'Remove this product?', destructive: true, confirmLabel: 'Remove' })) delPersisted.mutate(p.id); },
      }))
    : staged.map((p, i) => ({
        key: `staged-${i}`,
        itemCode: p.itemCode,
        description: p.description,
        quantity: p.quantity,
        unit: p.unit,
        onRemove: () => setStaged(staged.filter((_, j) => j !== i)),
      }));

  return (
    <div className="border-t pt-3">
      <div className="flex items-center justify-between mb-2">
        <Label>Product line items ({rows.length})</Label>
      </div>
      {rows.length === 0 && (
        <div className="text-xs text-muted-foreground py-2">
          {isEdit ? 'No products on this job.' : 'No products added yet.'}
        </div>
      )}
      {rows.length > 0 && (
        <>
          {/* Mobile / tablet: stacked cards. The desktop table doesn't
              shrink below ~600px without column clipping, so we swap to
              cards under `lg`. Mirrors the Schedule drag-create dialog
              (schedule.tsx) so both create paths look identical. */}
          <div className="lg:hidden space-y-2">
            {rows.map((r) => (
              <div key={r.key} className="border rounded-md p-3 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{r.itemCode ?? '—'}</div>
                  {r.description && <div className="text-xs text-muted-foreground truncate">{r.description}</div>}
                  <div className="text-xs text-muted-foreground mt-1">
                    Qty: <span className="text-foreground">{r.quantity ?? '—'}</span>
                    {r.unit ? <> · {r.unit}</> : null}
                  </div>
                </div>
                <button
                  type="button"
                  className="text-destructive text-lg leading-none px-2 py-1"
                  onClick={r.onRemove}
                  aria-label="Remove product"
                >×</button>
              </div>
            ))}
          </div>

          {/* Desktop: compact table for at-a-glance reading. */}
          <table className="hidden lg:table w-full text-xs border rounded overflow-hidden">
            <thead className="bg-muted/40 text-[11px]">
              <tr>
                <th className="text-left px-2 py-1">Item</th>
                <th className="text-left px-2 py-1">Description</th>
                <th className="text-right px-2 py-1 w-16">Qty</th>
                <th className="text-left px-2 py-1 w-16">Unit</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-t">
                  <td className="px-2 py-1">{r.itemCode ?? '—'}</td>
                  <td className="px-2 py-1 text-muted-foreground">{r.description ?? ''}</td>
                  <td className="px-2 py-1 text-right">{r.quantity ?? '—'}</td>
                  <td className="px-2 py-1">{r.unit ?? ''}</td>
                  <td className="px-2 py-1">
                    <button type="button" className="text-destructive hover:underline text-xs" onClick={r.onRemove}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* Add-row controls. On mobile the catalogue picker takes the full
          width and qty/unit share a 2-col row; on `lg` everything aligns
          into a single 4-col row. Touch target: h-11 on small, h-8 on
          `lg`. Mirrors schedule.tsx. */}
      <div className="mt-3 grid grid-cols-1 gap-2 lg:mt-2 lg:grid-cols-[1fr_80px_80px_auto] lg:gap-1">
        <LookupField
          endpoint="/catalogue-items"
          labelKey="itemCode"
          secondaryLabelKey="description"
          searchAllFields
          value={newItem?.id ?? null}
          onChange={(id) => { if (!id) setNewItem(null); }}
          onPick={(item) => {
            setNewItem(item);
            if (item?.unit) setNewUnit(item.unit);
          }}
          placeholder="Catalogue item…"
        />
        <div className="grid grid-cols-2 gap-2 lg:contents">
          <Input className="h-11 text-sm lg:h-8 lg:text-xs" type="number" step="0.01" placeholder="Qty" value={newQty} onChange={(e) => setNewQty(e.target.value)} />
          {/* Unit picker — DispatchJobProduct.unit is a plain string (legacy
              column kept that way for backwards-compat), so we translate the
              picked unit id → its `name` against the cached units list and
              store the name. */}
          <LookupField
            endpoint="/master-data/units"
            labelKey="name"
            value={units.find((u: any) => u.name === newUnit)?.id || null}
            onChange={(id) => {
              const name = id ? (units.find((u: any) => u.id === id)?.name ?? '') : '';
              setNewUnit(name);
            }}
            placeholder="Unit"
          />
        </div>
        <Button className="h-11 lg:h-8" type="button" disabled={!newItem || !newQty || addPersisted.isPending} onClick={addRow}>
          <Plus className="h-4 w-4 lg:h-3 lg:w-3" /> Add
        </Button>
      </div>
    </div>
  );
}

// afterSave hook companion — POSTs staged items once the parent has an id.
export async function persistStagedDispatchProducts(
  parentId: string,
  formValues: Record<string, any>,
  isCreate: boolean,
) {
  if (!isCreate) return;
  const staged: StagedProduct[] = formValues._stagedProducts ?? [];
  for (const p of staged) {
    const r = await api('/dispatch-job-products', {
      method: 'POST',
      body: JSON.stringify({
        dispatchJob: parentId,
        catalogueItem: p.catalogueItem,
        quantity: p.quantity,
        unit: p.unit ?? null,
      }),
    });
    if (!r.success) throw new Error(r.error?.message || 'Failed to add product');
  }
}
